import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampSelection } from "@/composants/ui/champ";
import { Vide } from "@/composants/ui/etats";
import { Donnee, Etiquette } from "@/composants/ui/etiquette";
import { euros, horodatage, typeProblemeFournisseur } from "@/lib/libelles";
import {
  api,
  type DemandeAvoir,
  type FicheCommandeFournisseur,
  type SaisieDemandeAvoir,
  type StatutDemandeAvoir,
} from "@/lib/api";

type Agir = (geste: () => Promise<FicheCommandeFournisseur>) => Promise<boolean>;

// ADR-0024 : l'état se lit en toutes lettres ; la couleur ne fait que le redire.
const STATUTS: Record<StatutDemandeAvoir, string> = {
  EN_NEGOCIATION: "En négociation",
  ATTENDUE: "Attendue",
  RECUE: "Reçue",
  VALIDEE: "Validée",
  ABANDONNEE: "Abandonnée",
};
const TONS: Record<StatutDemandeAvoir, "actif" | "neutre" | "succes"> = {
  EN_NEGOCIATION: "actif",
  ATTENDUE: "actif",
  RECUE: "succes",
  VALIDEE: "succes",
  ABANDONNEE: "neutre",
};

const TOUTE_LA_COMMANDE = "";

/** Ce qu'on fait d'une demande ; chaque geste dit s'il a abouti. */
export type GestesDemande = {
  abandonner: (motif: string) => Promise<boolean>;
  accepter: () => Promise<boolean>;
  corriger: (montant: number, motif?: string) => Promise<boolean>;
};

/** Les gestes sur la demande `demandeId` de la commande fournisseur `id`. */
export function gestesSur(id: string, demandeId: string, agir: Agir): GestesDemande {
  return {
    abandonner: (motif) => agir(() => api.abandonnerAvoir(id, demandeId, motif)),
    accepter: () => agir(() => api.accepterAvoir(id, demandeId)),
    corriger: (montant, motif) =>
      agir(() => api.corrigerMontantAvoir(id, demandeId, montant, motif)),
  };
}

function montantLu(saisie: string) {
  return Number(saisie.replace(",", ".").trim());
}

type Ouvert = "ABANDON" | "CORRECTION" | null;

/**
 * Une demande d'avoir fournisseur (LOT 2, tickets #44 et #47) : un montant que
 * LAB attend du fournisseur, pour une commande client. LABGEST n'envoie rien :
 * il la suit. En négociation, son montant se corrige, et le fournisseur
 * l'accepte : elle est attendue, son montant figé — sauf, exceptionnellement,
 * avec un motif, quand le fournisseur revient sur son engagement (ticket #49) ;
 * le motif de la dernière correction se lit. En cours, elle s'abandonne
 * avec un motif, et abandonnée elle reste lisible telle quelle. Sans la
 * permission de voir les montants, son montant ne s'affiche pas ; elle ne
 * touche pas le coût d'achat prévisionnel.
 */
export function DemandeAvoirDetail({
  demande,
  avecCommande = false,
  gestes,
}: {
  demande: DemandeAvoir;
  avecCommande?: boolean;
  gestes?: GestesDemande;
}) {
  const [ouvert, setOuvert] = useState<Ouvert>(null);
  const [motif, setMotif] = useState("");
  const [montant, setMontant] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const objet = demande.designation_ligne ?? `commande ${demande.numero_commande_client}`;
  const correctionComplete =
    montantLu(montant) > 0 && (!demande.motif_correction_exige || motif.trim() !== "");

  const faire = async (geste: () => Promise<boolean>) => {
    setEnvoi(true);
    const fait = await geste();
    setEnvoi(false);
    if (fait) {
      setMotif("");
      setMontant("");
      setOuvert(null);
    }
  };

  return (
    <article
      aria-label={`Demande d'avoir : ${demande.motif}`}
      className="flex flex-col gap-3 text-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-encre">{demande.motif}</p>
          <p className="text-encre-douce">
            {avecCommande ? (
              <>
                <Link
                  to={`/commandes-fournisseurs/${demande.commande_fournisseur_id}`}
                  className="text-encre underline underline-offset-4"
                >
                  Commande chez {demande.fournisseur}
                </Link>
                {" · "}
              </>
            ) : null}
            Pour <span className="font-mono">{demande.numero_commande_client}</span>
            {demande.designation_ligne ? <> — {demande.designation_ligne}</> : null}
            {" · "}demandée le {horodatage(demande.demandee_le)}
          </p>
        </div>
        <Etiquette ton={TONS[demande.statut]}>{STATUTS[demande.statut]}</Etiquette>
      </div>

      {demande.montant_ht !== null ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
          <Donnee
            libelle={demande.statut === "EN_NEGOCIATION" ? "Montant demandé" : "Montant attendu"}
          >
            <span className="tabular-nums">{euros(demande.montant_ht)} HT</span>
          </Donnee>
        </dl>
      ) : null}

      {demande.motif_correction ? (
        <p className="text-encre-douce">
          Montant corrigé le {horodatage(demande.corrigee_le)} : {demande.motif_correction}
        </p>
      ) : null}

      {demande.statut === "ABANDONNEE" ? (
        <p className="text-encre-douce">
          Abandonnée le {horodatage(demande.abandonnee_le)} : {demande.motif_abandon}
        </p>
      ) : null}

      {gestes && ouvert === "CORRECTION" ? (
        <form
          aria-label={`Corriger le montant de la demande d'avoir : ${objet}`}
          className="flex flex-col gap-2"
          onSubmit={async (evenement) => {
            evenement.preventDefault();
            if (!correctionComplete) return;
            await faire(() =>
              gestes.corriger(
                montantLu(montant),
                demande.motif_correction_exige ? motif.trim() : undefined,
              ),
            );
          }}
        >
          <Champ
            libelle="Nouveau montant HT (€)"
            required
            inputMode="decimal"
            value={montant}
            onChange={(e) => setMontant(e.target.value)}
          />
          {demande.motif_correction_exige ? (
            <Champ
              libelle="Motif de la correction"
              aide="Le fournisseur a accepté ce montant : dites pourquoi il revient sur son engagement."
              required
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Bouton type="submit" disabled={envoi || !correctionComplete}>
              Corriger le montant
            </Bouton>
            <Bouton type="button" variante="discret" onClick={() => setOuvert(null)}>
              Annuler
            </Bouton>
          </div>
        </form>
      ) : gestes && ouvert === "ABANDON" ? (
        <form
          aria-label={`Abandonner la demande d'avoir : ${objet}`}
          className="flex flex-col gap-2"
          onSubmit={async (evenement) => {
            evenement.preventDefault();
            if (!motif.trim()) return;
            await faire(() => gestes.abandonner(motif.trim()));
          }}
        >
          <Champ
            libelle="Motif de l'abandon"
            required
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Bouton type="submit" disabled={envoi || !motif.trim()}>
              Abandonner la demande
            </Bouton>
            <Bouton type="button" variante="discret" onClick={() => setOuvert(null)}>
              Annuler
            </Bouton>
          </div>
        </form>
      ) : gestes &&
        (demande.acceptable || demande.montant_corrigeable || demande.abandonnable) ? (
        <div className="flex flex-wrap gap-2">
          {demande.acceptable ? (
            <Bouton
              taille="compact"
              disabled={envoi}
              onClick={() => faire(() => gestes.accepter())}
            >
              Le fournisseur accepte
            </Bouton>
          ) : null}
          {demande.montant_corrigeable ? (
            <Bouton
              taille="compact"
              variante="discret"
              onClick={() => {
                setMotif("");
                setOuvert("CORRECTION");
              }}
            >
              Corriger le montant…
            </Bouton>
          ) : null}
          {demande.abandonnable ? (
            <Bouton
              taille="compact"
              variante="discret"
              onClick={() => {
                setMotif("");
                setOuvert("ABANDON");
              }}
            >
              Abandonner…
            </Bouton>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

/**
 * La carte des demandes d'avoir sur la fiche d'une commande fournisseur. Une
 * demande se pose sur une commande partie, pour une commande client où
 * l'appelant agit — par l'une de ses lignes, ou pour la commande entière —, et
 * peut désigner le problème fournisseur d'où elle vient. Le serveur dit si
 * l'appelant peut en demander (`demande_avoir_possible`) et en abandonner une.
 */
export function DemandesAvoirDeLaCommande({
  fiche,
  agir,
}: {
  fiche: FicheCommandeFournisseur;
  agir: Agir;
}) {
  const [ouvert, setOuvert] = useState(false);
  if (fiche.demandes_avoir.length === 0 && !fiche.demande_avoir_possible) return null;

  return (
    <Carte>
      <EnTeteCarte className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-encre">Demandes d'avoir</h2>
        {fiche.demande_avoir_possible && !ouvert ? (
          <Bouton taille="compact" variante="discret" onClick={() => setOuvert(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Demander un avoir
          </Bouton>
        ) : null}
      </EnTeteCarte>
      {ouvert ? (
        <CorpsCarte className="border-b border-filet">
          <FormulaireDemandeAvoir
            fiche={fiche}
            onAnnuler={() => setOuvert(false)}
            onValider={async (saisie) => {
              if (await agir(() => api.demanderAvoir(fiche.id, saisie))) setOuvert(false);
            }}
          />
        </CorpsCarte>
      ) : null}
      {fiche.demandes_avoir.length === 0 ? (
        <Vide
          titre="Aucune demande d'avoir"
          detail="Une remise négociée, un surplus gardé, un geste commercial : LABGEST suit ce que le fournisseur doit, il ne le lui envoie pas."
        />
      ) : (
        <ul className="divide-y divide-filet">
          {fiche.demandes_avoir.map((demande) => (
            <li key={demande.id} className="px-4 py-3">
              <DemandeAvoirDetail
                demande={demande}
                gestes={gestesSur(fiche.id, demande.id, agir)}
              />
            </li>
          ))}
        </ul>
      )}
    </Carte>
  );
}

function FormulaireDemandeAvoir({
  fiche,
  onAnnuler,
  onValider,
}: {
  fiche: FicheCommandeFournisseur;
  onAnnuler: () => void;
  onValider: (saisie: SaisieDemandeAvoir) => Promise<void>;
}) {
  const servies = fiche.commandes_clients.filter((servie) => servie.intervenable);
  const [commandeClient, setCommandeClient] = useState(servies.length === 1 ? servies[0].id : "");
  const [ligne, setLigne] = useState(TOUTE_LA_COMMANDE);
  const [probleme, setProbleme] = useState("");
  const [montant, setMontant] = useState("");
  const [motif, setMotif] = useState("");
  const [attendue, setAttendue] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const lignes = fiche.lignes.filter((l) => l.commande_client_id === commandeClient);
  // Un problème posé sur une ligne est celui de sa commande client, et de la
  // ligne de la demande quand elle en désigne une (la base le vérifie).
  const problemes = fiche.problemes.filter((p) =>
    p.ligne_commande_fournisseur_id === null
      ? true
      : ligne === TOUTE_LA_COMMANDE
        ? lignes.some((l) => l.id === p.ligne_commande_fournisseur_id)
        : p.ligne_commande_fournisseur_id === ligne,
  );
  const complet = commandeClient !== "" && montantLu(montant) > 0 && motif.trim() !== "";

  return (
    <form
      aria-label="Demander un avoir"
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        if (!complet) return;
        setEnvoi(true);
        await onValider({
          commande_client_id: commandeClient,
          ligne_commande_fournisseur_id: ligne === TOUTE_LA_COMMANDE ? null : ligne,
          probleme_commande_fournisseur_id: probleme || null,
          montant_ht: montantLu(montant),
          motif: motif.trim(),
          attendue,
        });
        setEnvoi(false);
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <ChampSelection
          libelle="Commande client"
          required
          options={[
            ...(servies.length === 1 ? [] : [{ valeur: "", libelle: "Choisir une commande" }]),
            ...servies.map((servie) => ({
              valeur: servie.id,
              libelle: servie.numero_commande_client,
            })),
          ]}
          value={commandeClient}
          onChange={(e) => {
            setCommandeClient(e.target.value);
            setLigne(TOUTE_LA_COMMANDE);
            setProbleme("");
          }}
        />
        <ChampSelection
          libelle="Ligne"
          options={[
            { valeur: TOUTE_LA_COMMANDE, libelle: "Aucune : la commande client entière" },
            ...lignes.map((l) => ({ valeur: l.id, libelle: l.designation })),
          ]}
          value={ligne}
          onChange={(e) => {
            setLigne(e.target.value);
            setProbleme("");
          }}
        />
      </div>
      {problemes.length > 0 ? (
        <ChampSelection
          libelle="Problème fournisseur d'origine"
          options={[
            { valeur: "", libelle: "Aucun" },
            ...problemes.map((p) => ({
              valeur: p.id,
              libelle: `${typeProblemeFournisseur(p.type)} : ${p.description}`,
            })),
          ]}
          value={probleme}
          onChange={(e) => setProbleme(e.target.value)}
        />
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ
          libelle="Montant HT attendu (€)"
          required
          inputMode="decimal"
          value={montant}
          onChange={(e) => setMontant(e.target.value)}
        />
        <Champ libelle="Motif" required value={motif} onChange={(e) => setMotif(e.target.value)} />
      </div>
      <label className="flex items-center gap-2 text-sm text-encre">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={attendue}
          onChange={(e) => setAttendue(e.target.checked)}
        />
        Le fournisseur l'a déjà acceptée : elle est attendue
      </label>
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !complet}>
          Demander l'avoir
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

/**
 * Les demandes d'avoir d'une commande client, sur sa fiche : en lecture, avec
 * le chemin de la commande fournisseur où elles se gèrent.
 */
export function DemandesAvoirDuDossier({ demandes }: { demandes: DemandeAvoir[] }) {
  if (demandes.length === 0) return null;
  return (
    <Carte>
      <EnTeteCarte>
        <h2 className="text-sm font-semibold text-encre">Demandes d'avoir fournisseur</h2>
      </EnTeteCarte>
      <ul className="divide-y divide-filet">
        {demandes.map((demande) => (
          <li key={demande.id} className="px-4 py-3">
            <DemandeAvoirDetail demande={demande} avecCommande />
          </li>
        ))}
      </ul>
    </Carte>
  );
}
